import { LoadingState } from "@repo/app-types/client/client";
import type { ApiKey } from "@repo/app-types/entities/apiKey";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Modal } from "@repo/ui/Modal/modal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { type FC, useState } from "react";
import { getAxiosInstanceWithAuth } from "../../../api/axiosConfig.js";
import { useAppDispatch } from "../../../hooks/hook.js";

interface DeleteApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  apiKeyToDelete: ApiKey | null;
}

export const DeleteApiKeyModal: FC<DeleteApiKeyModalProps> = ({
  isOpen,
  onClose,
  apiKeyToDelete,
}) => {
  const dispatch = useAppDispatch();
  const [deleteApiKeyLoadingState, setDeleteApiKeyLoadingState] =
    useState<LoadingState>(LoadingState.LOADED);

  const onClickDeleteApiKey = async () => {
    if (!apiKeyToDelete) return;

    setDeleteApiKeyLoadingState(LoadingState.LOADING);
    try {
      const axios = await getAxiosInstanceWithAuth();
      const { data } = await axios.delete(`/api-keys/${apiKeyToDelete.id}`);

      setDeleteApiKeyLoadingState(LoadingState.LOADED);
      onClose();
    } catch (err) {
      setDeleteApiKeyLoadingState(LoadingState.ERROR);
    }
  };

  return (
    <Modal isOpen={isOpen} onCancel={onClose}>
      <ModalHeader onClose={onClose}>Are you sure?</ModalHeader>
      <div className="mt-5 text-sm">
        {`Deleting the "${apiKeyToDelete?.name}" API key will revoke API access for all services using it. Are you sure you want to continue?`}
      </div>
      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <div className="ml-3">
          <Button
            variant={ButtonVariant.Warning}
            isDisabled={deleteApiKeyLoadingState === LoadingState.LOADING}
            onClick={() => onClickDeleteApiKey()}
            isLoading={deleteApiKeyLoadingState === LoadingState.LOADING}
          >
            Delete API Key
          </Button>
        </div>
        <Button variant={ButtonVariant.Secondary} onClick={() => onClose()}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
};
